/* eslint-disable @typescript-eslint/no-use-before-define */
import { defineComponent } from 'vue';
import { defineModule } from 'direct-vuex';
import isEqual from 'lodash/isEqual';
import { moduleActionContext, moduleGetterContext } from '@/plugins/vuex';
import { SourceOptions, adapters } from '@aison/migrator';

import { database, QueryRequest } from '@/api';
import { flattenKeys } from '@/utils';
import { SimpleTransformField } from '@/types';

export interface ReadState {
  loading: boolean;
  error?: object;
  read: adapters.AdapterReadOptions[];
  currentIndex: number;
  data: object[];
  dataOptions?: adapters.AdapterReadOptions;
  // stringQuery: string;
}

const initialState = (): ReadState => ({
  loading: false,
  error: undefined,
  read: [],
  currentIndex: 0,
  data: [],
  dataOptions: undefined,
  // stringQuery: '',
});

const module = defineModule({
  namespaced: true,
  state: initialState,
  getters: {
    currentRead(...args) {
      const { state } = readGetterContext(args);
      return state.read[state.currentIndex];
    },
    dataLoaded: (...args) => !!readGetterContext(args).state.data.length,
    flatReadResult(...args) {
      const { data } = readGetterContext(args).state;
      if (data.length) {
        return data.map(item => flattenKeys(item));
      }

      return data;
    },
    fields(...args): SimpleTransformField[] {
      const { data } = readGetterContext(args).state;
      if (!data.length) {
        return [];
      }

      const item = flattenKeys(data[0]);

      return Object.entries(item).map(([key, value]) => ({
        value: key,
        text: key,
        subtitle:
          typeof value === 'string' && Date.parse(value)
            ? 'date'
            : typeof value,
      }));
    },
    readRequest(...args): QueryRequest {
      const { getters, rootGetters } = readGetterContext(args);
      return {
        ...rootGetters.source.connectRequest,
        read: {
          ...getters.currentRead,
          run: true,
        },
      };
    },
    dataMatchesCurrentRead(...args): boolean {
      const { state, getters, rootState } = readGetterContext(args);

      switch (rootState.source.type) {
        case 'mysql':
        case 'postgresql':
        case 'mssql':
          return getters.currentRead?.query === state.dataOptions?.query;
        case 'mongodb':
          return (
            isEqual(
              getters.currentRead?.collection,
              state.dataOptions?.collection
            ) &&
            isEqual(getters.currentRead?.command, state.dataOptions?.command)
          );
        case undefined:
          return true;
        default:
          throw new Error(
            'Read result matcher not implemented for type: ' +
              rootState.source.type
          );
      }
    },
    currentQuery(...args): string {
      const { getters } = readGetterContext(args);
      return getters.currentRead?.query || '';
    },
    isQueryEmpty(...args) {
      const { state } = readGetterContext(args);
      return (index: number) => !state.read[index]?.query;
    },
    hasRunQuery(...args) {
      const { state } = readGetterContext(args);
      return state.read.some(read => read.run);
    },
  },
  mutations: {
    reset(state, options = {}) {
      Object.assign(state, initialState(), options);
    },
    setReadLoading(state) {
      state.loading = true;
      state.error = undefined;
      state.data = [];
      state.dataOptions = undefined;
    },
    setReadError(state, error: object) {
      state.loading = false;
      state.error = error;
    },
    setReadResult(state, data: object[]) {
      state.loading = false;
      state.error = undefined;
      state.data = data;
      state.dataOptions = { ...state.read[state.currentIndex] };
    },
    addRead(state, read: adapters.AdapterReadOptions) {
      state.read.push(read);
      state.currentIndex = state.read.length - 1;
    },
    updateRead(
      state,
      payload: { index?: number; read: adapters.AdapterReadOptions }
    ) {
      const index = Number.isInteger(payload.index as number)
        ? payload.index
        : state.currentIndex;
      const merged = {
        ...state.read[index as number],
        ...payload.read,
      };

      state.read[index as number] = merged;
    },
    // updateStringQuery(state, payload: string) {
    //   state.stringQuery = payload;
    // },
    deleteRead(state, index: number) {
      if (index < state.read.length) {
        if (index === state.currentIndex) {
          if (index === state.read.length - 1) {
            state.currentIndex = index - 1;
          }
        }
        delete state.read[index];
      } else {
        // eslint-disable-next-line no-console
        console.error(
          `tried deleting read at index ${index}, but state.read length is ${state.read.length}`
        );
      }
    },
    setCurrentReadIndex(state, index: number) {
      if (index < state.read.length) {
        state.currentIndex = index;
      } else {
        // eslint-disable-next-line no-console
        console.error(
          `tried setting current read to index ${index}, but state.read length is ${state.read.length}`
        );
      }
    },
    resetReadResult(state) {
      state.data = [];
      state.dataOptions = undefined;
    },
  },
  actions: {
    reset(context, payload: SourceOptions) {
      const { commit } = readActionContext(context);
      // TODO: Parse values into options properlyyy
      commit.reset({
        queries: payload.read,
      });
    },
    newRead(context) {
      const { commit, rootState } = readActionContext(context);

      const read = { run: true };
      switch (rootState.source.type) {
        case 'mongodb':
          break;
        default:
          read['run'] = true;
      }

      commit.addRead(read);
    },
    async read(context) {
      const { commit, getters, dispatch } = readActionContext(context);
      // dispatch.parseQuery(null);
      commit.setReadLoading(null);
      try {
        const result = await database.read(getters.readRequest);
        commit.setReadResult(result);
      } catch (error) {
        if (error?.message) {
          commit.setReadError(error.message);
        } else {
          commit.setReadError(error);
        }
      }
    },
    setCurrentReadIndex(context, payload: number) {
      const { commit, dispatch } = readActionContext(context);
      commit.setCurrentReadIndex(payload);
      // dispatch.stringifyQuery(null);
    },
    setCurrentQuery(context, payload: string) {
      const { commit } = readActionContext(context);
      commit.updateRead({ read: { query: payload } });
    },
    // Disabled as of now, as it introduces some issues, and is not needed
    // unless working with mongodb
    // setStringQuery(context, payload: string) {
    //   const { commit, dispatch } = readActionContext(context);
    //   commit.updateStringQuery(payload);
    //   dispatch.debouncedParseQuery(null);
    // },
    // debouncedParseQuery: debounce(context => {
    //   const { dispatch } = readActionContext(context);
    //   dispatch.parseQuery(null);
    // }, 1000),
    // parseQuery(context) {
    //   const { state, commit, rootState } = readActionContext(context);
    //   let read;
    //   switch (rootState.source.type) {
    //     case 'mongodb':
    //       if (!state.stringQuery.trim()) {
    //         read = '';
    //       } else {
    //         try {
    //           read = JSON.parse(state.stringQuery);
    //         } catch (e) {
    //           // eslint-disable-next-line no-console
    //           console.warn('failed to parse', e);
    //         }
    //       }
    //       break;
    //     default:
    //       read = { query: state.stringQuery };
    //   }
    //
    //   if (read) {
    //     commit.updateRead({ read });
    //   }
    // },
    // stringifyQuery(context) {
    //   const { getters, commit, rootState } = readActionContext(context);
    //   let query;
    //   switch (rootState.source.type) {
    //     case 'mongodb':
    //       query = JSON.stringify(omit(getters.currentRead, ['run']), null, 2);
    //       break;
    //     default:
    //       query = getters.currentRead?.query;
    //   }
    //
    //   // if (read) {
    //   //   commit.updateRead({ read });
    //   // }
    //   commit.updateStringQuery(query || '');
    // },
    resetReadResult(context) {
      const { commit } = readActionContext(context);
      commit.resetReadResult(null);
    },
  },
});

const readGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, module);
const readActionContext = (context: any) =>
  moduleActionContext(context, module);

export default module;
